import type { AudioOnset, ChartAudio, ChartPalette } from '../chart.js';
import { buildVariantTitle } from '../chart.js';
import { type Difficulty, type Variant } from '../gameTypes.js';
import type {
  ImportedCircleBundle,
  ImportedCircleEntry,
  ImportedOnsetsFile,
  PerVariantTimingArg,
} from '../circlesBuiltinMaps/importedOsuChart.js';
import { withFillerCircleEntries } from '../circlesBuiltinMaps/importedOsuChart.js';
import { jumpApexLeadBeats, JUMP_VARIANT_TUNING, type JumpChart } from '../jumpChart.js';
import { jumpFlowSlug } from '../jumpMapRevision.js';
import {
  createJumpCharGrid,
  packJumpGrid,
  paintFloor,
  JUMP_CELL_BEATS,
  JUMP_CELL_PX,
  JUMP_GRID_ROWS,
  JUMP_GROUND_ROWS,
  type JumpGrid,
} from '../jumpGrid.js';
import { filterUnclearableJumpGrid } from './jumpGridFilter.js';
import type { JumpAnchor } from './patterns/jumpPatternBase.js';
import { composeJumpGrid } from './patterns/jumpPatternComposer.js';

/**
 * Jump-mode chart derivation from the shared Circles osu JSON. The song's hit
 * objects become the **onset spine** — the beats the player presses on, kept the
 * genre-correct distance apart by {@link jumpIntensityFilter} — and a
 * pattern-based composer ({@link composeJumpGrid}) choreographs reusable
 * Geometry-Dash-style PATTERNS (lone hops, stutters, spike-walls, staircases,
 * platforms, corridors, saws, gauntlets) onto that spine. Every required jump
 * still lands on a real onset, so the chart stays in time with the music while
 * playing like a designed platformer rather than a one-spike-per-beat transcript.
 *
 * Fully deterministic per (slug, variant): the server re-derives the identical
 * grid for replay validation instead of persisting a blob. Difficulty is
 * density-driven — harder variants keep more onsets (smaller `minGapBeats`) and
 * unlock denser patterns/wider walls, never a faster scroll.
 */

/**
 * Opening "warm-up" window (beats) over which the intensity filter eases from a
 * wide gap to the steady-state `minGapBeats` — the GD/Impossible "start easy,
 * then ramp" opening.
 */
const WARMUP_BEATS = 16;

/** Gap multiplier at the very start of {@link WARMUP_BEATS} (decays to 1). */
const WARMUP_START_GAP_MUL = 1.7;

/**
 * Re-express onsets stored in the anchor variant's beat space into a different
 * variant's beat space. Mirrors the Waves/Circles helper, kept private so Jump
 * doesn't depend on a circles-internal export.
 */
function rescaleOnsets(
  anchorBpm: number,
  anchorOffsetMs: number,
  onsets: ReadonlyArray<{ beat: number; kind: string; intensity: number }>,
  toBpm: number,
  toOffsetMs: number,
): AudioOnset[] {
  const out: AudioOnset[] = [];
  for (const o of onsets) {
    const realMs = (o.beat * 60000) / anchorBpm + anchorOffsetMs;
    if (realMs < toOffsetMs) {
      continue;
    }
    const beat = ((realMs - toOffsetMs) * toBpm) / 60000;
    const kind: AudioOnset['kind'] = o.kind === 'kick' || o.kind === 'snare' ? o.kind : 'lead';
    out.push({ beat: Math.round(beat * 1000) / 1000, kind, intensity: o.intensity });
  }
  return out;
}

/** Translate imported osu hit-object records into Jump's onset spine. */
function normaliseJumpHits(circles: ReadonlyArray<ImportedCircleEntry>): JumpAnchor[] {
  const out: JumpAnchor[] = [];
  for (const c of circles) {
    if (c.kind === 'spinner') {
      if (c.spinnerEndBeat === undefined || c.spinnerEndBeat <= c.beat) {
        out.push({ beat: c.beat, kind: 'circle', endBeat: c.beat });
        continue;
      }
      out.push({ beat: c.beat, kind: 'spinner', endBeat: c.spinnerEndBeat });
      continue;
    }
    if (c.kind === 'slider') {
      if (!c.slider || c.slider.endBeat <= c.beat) {
        out.push({ beat: c.beat, kind: 'circle', endBeat: c.beat });
        continue;
      }
      out.push({ beat: c.beat, kind: 'slider', endBeat: c.slider.endBeat });
      continue;
    }
    out.push({ beat: c.beat, kind: 'circle', endBeat: c.beat });
  }
  return out;
}

/**
 * Thin a dense hit list so consecutive required jumps are never closer than the
 * variant's `minGapBeats` (you can't re-jump mid-air). Spacing is measured
 * against the previous KEPT hit's END and widened during the opening warm-up.
 * Smaller `minGapBeats` (harder variants) keeps more hits → denser charts — the
 * difficulty-monotonicity lever.
 */
export function jumpIntensityFilter(variant: Variant, hits: ReadonlyArray<JumpAnchor>): JumpAnchor[] {
  const minGap = JUMP_VARIANT_TUNING[variant].minGapBeats;
  const requiredGapAt = (beat: number): number => {
    const ramp = Math.max(0, 1 - beat / WARMUP_BEATS);
    return minGap * (1 + (WARMUP_START_GAP_MUL - 1) * ramp);
  };
  const out: JumpAnchor[] = [];
  let lastKeptEnd = -Infinity;
  for (const h of hits) {
    if (h.beat - lastKeptEnd < requiredGapAt(h.beat)) {
      continue;
    }
    out.push(h);
    lastKeptEnd = h.endBeat;
  }
  return out;
}

interface BuildGridArgs {
  variant: Variant;
  bundle: ImportedCircleBundle;
  totalBeats: number;
  slug: string;
}

/**
 * Build the variant's tile grid: derive the onset spine from the song, then let
 * the pattern composer choreograph obstacles onto it. The composer keeps terrain
 * conservative-by-construction, but onset-driven spike/saw arrangements can still
 * be unthreadable, so the packed grid is run through {@link filterUnclearableJumpGrid}
 * before it ships. Fully deterministic per (slug, variant).
 */
export function buildJumpGridForVariant(args: BuildGridArgs): JumpGrid {
  const { variant, bundle, totalBeats, slug } = args;
  const tuning = JUMP_VARIANT_TUNING[variant];
  // Beats from press to apex for each jump — the composer's stamper shifts each
  // obstacle right by the lead of the jump it's MEANT for, so a press on the
  // onset apexes over it (the music-sync contract).
  const longApexLead = jumpApexLeadBeats(tuning.gravity, tuning.jumpVelocity);
  const shortApexLead = jumpApexLeadBeats(tuning.gravity, tuning.shortJumpVelocity);
  const cellBeats = JUMP_CELL_BEATS;
  const cols = Math.max(1, Math.ceil(totalBeats / cellBeats));

  const kiai = bundle.kiaiSections ?? [];
  const inKiaiAt = (beat: number): boolean => kiai.some((s) => beat >= s.startBeat && beat < s.endBeat);

  const allHits = normaliseJumpHits(bundle.circles).sort((a, b) => a.beat - b.beat);
  const anchors = jumpIntensityFilter(variant, allHits);

  const charGrid = createJumpCharGrid(JUMP_GRID_ROWS, cols);
  // Baseline ground everywhere; patterns carve / raise / decorate it.
  paintFloor(charGrid, 0, cols, JUMP_GROUND_ROWS);

  composeJumpGrid({
    charGrid,
    cols,
    anchors,
    totalBeats,
    cellBeats,
    longApexLead,
    shortApexLead,
    minGapBeats: tuning.minGapBeats,
    maxClusterCols: tuning.maxClusterCols,
    variant,
    slug,
    inKiaiAt,
  });

  const grid = packJumpGrid(charGrid, cellBeats, JUMP_CELL_PX);
  return filterUnclearableJumpGrid(grid, args);
}

export interface AssembleJumpArgs {
  baseSlug: string;
  baseTitle: string;
  description: string;
  difficulty: Difficulty;
  palette: ChartPalette;
  attribution: string;
  mapper: string;
  /** See {@link ImportedOnsetsFile}. Anchor (normal) timing the onsets were extracted against. */
  anchorOnsets: ImportedOnsetsFile;
  /** The three imported osu diffs (normal/hard/extreme), reused verbatim from Circles. */
  imported: Record<Variant, ImportedCircleBundle>;
  /** Per-variant `(bpm, offsetMs, totalBeats)` timing — same triples Waves/Circles use. */
  timing: Record<Variant, PerVariantTimingArg>;
}

/**
 * Build all three JumpChart variants for a track from its Circles imported JSON.
 * Fully deterministic — identical bundles always yield identical grids, so the
 * server re-derives the chart for replay validation instead of persisting a blob.
 * Audio points straight at the existing `/audio/circles/<slug>.mp3` (no copy).
 */
export function assembleJumpChartFromCircles(args: AssembleJumpArgs): Record<Variant, JumpChart> {
  const {
    baseSlug,
    baseTitle,
    description,
    difficulty,
    palette,
    attribution,
    mapper,
    anchorOnsets,
    imported,
    timing,
  } = args;

  const audioSrc = `/audio/circles/${baseSlug}.mp3`;
  const variants: Variant[] = ['normal', 'hard', 'extreme'];
  const out = {} as Record<Variant, JumpChart>;

  for (const variant of variants) {
    const t = timing[variant];
    const tuning = JUMP_VARIANT_TUNING[variant];
    const audio: ChartAudio = {
      src: audioSrc,
      bpm: t.bpm,
      offsetMs: t.offsetMs,
      durationSec: anchorOnsets.durationSec,
      attribution,
      onsets: rescaleOnsets(anchorOnsets.bpm, anchorOnsets.offsetMs, anchorOnsets.onsets, t.bpm, t.offsetMs),
    };
    // Cap chart beats to the audio duration so the music never ends before the
    // chart, then round to a whole column so the grid tiles it exactly.
    const audioBeats = (audio.durationSec * t.bpm) / 60;
    const cappedBeats = Math.min(t.totalBeats, Math.floor(audioBeats));
    const cols = Math.max(1, Math.ceil(cappedBeats / JUMP_CELL_BEATS));
    const totalBeats = cols * JUMP_CELL_BEATS;
    // Backfill long silent stretches with the shared beat-grid filler.
    const filledBundle: ImportedCircleBundle = {
      ...imported[variant],
      circles: withFillerCircleEntries(imported[variant].circles, totalBeats),
    };
    const grid = buildJumpGridForVariant({ variant, bundle: filledBundle, totalBeats, slug: baseSlug });

    out[variant] = {
      mode: 'jump',
      id: jumpFlowSlug(baseSlug, variant),
      slug: jumpFlowSlug(baseSlug, variant),
      title: buildVariantTitle(baseTitle, variant),
      description,
      bpm: t.bpm,
      totalBeats,
      difficulty,
      variant,
      palette,
      audio,
      mapper,
      gravity: tuning.gravity,
      jumpVelocity: tuning.jumpVelocity,
      shortJumpVelocity: tuning.shortJumpVelocity,
      playerSize: tuning.playerSize,
      grid,
    };
  }
  return out;
}
